import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { Box, IconButton, List, Stack, Typography } from '@mui/material';

import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

import { usePhoneScreen } from '@/designs-dev/device/PhoneScreenContext';
import { APP_HEADER_H } from '@/karehero-app/shell/layout';
import ConversationRow from '@/karehero-app/tell-us-once/intake/ConversationRow';
import { HOME_HISTORY } from '@/karehero-app/tell-us-once/intake/content.v2-0';

const SLIDE_MS = 260;

/**
 * The conversations, from the menu in the chat Home's bar: a panel from the
 * left over the chat, the way a chat app opens its history. A new one at the
 * top, then the ones so far. Tapping anything just closes it for now.
 */
const ChatHistoryDrawer = ({
  onClose,
  threads = HOME_HISTORY.threads,
  onNew,
  onThread,
}: {
  onClose: () => void;
  /** Whose conversations: one person's, where the app is about one at a time. */
  threads?: { title: string }[];
  /** New conversation tapped. Closes the drawer either way. */
  onNew?: () => void;
  /** A past conversation tapped, by its title. */
  onThread?: (title: string) => void;
}) => {
  const [open, setOpen] = useState(false);
  // Open on the frame after mount, so the closed position paints first and
  // the slide plays. BottomSheet's approach.
  useEffect(() => {
    const r = requestAnimationFrame(() =>
      requestAnimationFrame(() => setOpen(true)),
    );
    return () => cancelAnimationFrame(r);
  }, []);
  const close = () => {
    setOpen(false);
    window.setTimeout(onClose, SLIDE_MS);
  };
  const screenEl = usePhoneScreen();

  const drawer = (
    <>
      <Box
        onClick={close}
        sx={{
          position: 'absolute',
          inset: 0,
          bgcolor: 'common.black',
          opacity: open ? 0.4 : 0,
          transition: `opacity ${SLIDE_MS}ms ease`,
          zIndex: 30,
        }}
      />
      <Stack
        sx={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: 0,
          width: '82%',
          // The status bar's height, so the header below lands where the app
          // bar's does.
          pt: '47px',
          bgcolor: 'background.default',
          borderRight: 1,
          borderColor: 'custom.neutral.200',
          zIndex: 31,
          transform: open ? 'translateX(0)' : 'translateX(-100%)',
          transition: `transform ${SLIDE_MS}ms ease`,
        }}
      >
        {/* The app bar's height with its rule under it, so the drawer's
            header lines up with the bar it opened over. */}
        <Stack
          sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            minHeight: APP_HEADER_H,
            flexShrink: 0,
            borderBottom: 1,
            borderColor: 'custom.neutral.200',
          }}
          direction='row'
        >
          {/* The logo and the name, since the logo left the bar for the page. */}
          <Stack sx={{ alignItems: 'center' }} direction='row' spacing={1.25}>
            <Box
              sx={{ width: 30, height: 'auto' }}
              component='img'
              src='/vector/karehero-logo.png'
              alt=''
            />
            <Typography sx={{ color: 'primary.main' }} variant='h4'>
              {HOME_HISTORY.title}
            </Typography>
          </Stack>
          <IconButton aria-label='Close' onClick={close}>
            <Icon icon='xmark' fontSize={18} />
          </IconButton>
        </Stack>
        <List>
          <ConversationRow
            label={HOME_HISTORY.newChat}
            isNew
            onClick={() => {
              close();
              onNew?.();
            }}
          />
        </List>
        {/* A plain label, not ListSubheader: that paints its own paper
            background, which showed as a white bar on the page colour. */}
        <Typography
          sx={{ color: 'text.secondary', px: 2, pt: 2 }}
          variant='subtitle2'
        >
          {HOME_HISTORY.recent}
        </Typography>
        <List>
          {threads.map((t) => (
            <ConversationRow
              key={t.title}
              label={t.title}
              onClick={() => {
                close();
                onThread?.(t.title);
              }}
            />
          ))}
        </List>
      </Stack>
    </>
  );

  return screenEl ? createPortal(drawer, screenEl) : drawer;
};

export default ChatHistoryDrawer;
